import React, { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '../components/layout/AdminLayout';
import { placementStatsAPI, collegesAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Search,
  TrendingUp,
  Building2,
  GraduationCap,
  Edit,
  Plus,
  X,
  ChevronDown,
  ChevronRight,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
// Course Levels
const COURSE_LEVELS = [
  { value: 'UG', label: 'UG (Undergraduate)' },
  { value: 'PG', label: 'PG (Postgraduate)' },
  { value: 'Diploma', label: 'Diploma' },
  { value: 'Doctorial', label: 'Doctoral/PhD' },
  { value: 'Advanced Masters', label: 'Advanced Masters' },
  { value: 'Others', label: 'Others' },
];

// Generate year options (current year to 10 years back, plus next year)
const generateYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  // Add next year
  years.push(`${currentYear + 1}-${(currentYear + 2).toString().slice(-2)}`);
  // Add current and past 10 years
  for (let i = 0; i <= 10; i++) {
    const startYear = currentYear - i;
    const endYear = startYear + 1;
    years.push(`${startYear}-${endYear.toString().slice(-2)}`);
  }
  return years;
};

const YEAR_OPTIONS = generateYearOptions();


export default function PlacementStats() {
  const [stats, setStats] = useState([]);
  const [allColleges, setAllColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedColleges, setExpandedColleges] = useState({});
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCollege, setEditingCollege] = useState(null);
  const [editingStats, setEditingStats] = useState([]);
  const [saving, setSaving] = useState(false);
  
  // Add new dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedCollegeId, setSelectedCollegeId] = useState('');
  const [newPlacementData, setNewPlacementData] = useState([
    {
      level: 'UG',
      stats: [{
        year: new Date().getFullYear().toString(),
        highest_package: '',
        average_package: '',
        median_package: '',
        placement_rate: '',
        total_offers: '',
        companies_visited: '',
        top_recruiters: ''
      }]
    }
  ]);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const [statsRes, collegesRes] = await Promise.all([
        placementStatsAPI.getAll(searchQuery || undefined),
        collegesAPI.getAll({})
      ]);
      setStats(statsRes.data?.placement_stats || []);
      setAllColleges(collegesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load placement statistics');
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchStats();
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchStats]);

  const toggleExpanded = (collegeId) => {
    setExpandedColleges(prev => ({
      ...prev,
      [collegeId]: !prev[collegeId]
    }));
  };

  // Edit handlers
  const handleEdit = (college) => {
    setEditingCollege(college);
    const existingData = college.placement_data || [];
    // Ensure both UG and PG levels exist
    const ugData = existingData.find(d => d.level === 'UG') || { level: 'UG', stats: [] };
    const pgData = existingData.find(d => d.level === 'PG') || { level: 'PG', stats: [] };
    setEditingStats([ugData, pgData]);
    setEditDialogOpen(true);
  };

  const handleAddYearToLevel = (levelIndex) => {
    const newStats = [...editingStats];
    newStats[levelIndex].stats.push({
      year: new Date().getFullYear().toString(),
      highest_package: '',
      average_package: '',
      median_package: '',
      placement_rate: '',
      total_offers: '',
      companies_visited: '',
      top_recruiters: ''
    });
    setEditingStats(newStats);
  };

  const handleStatChange = (levelIndex, statIndex, field, value) => {
    const newStats = [...editingStats];
    if (field === 'placement_rate' || field === 'total_offers' || field === 'companies_visited') {
      newStats[levelIndex].stats[statIndex][field] = value === '' ? null : parseFloat(value);
    } else {
      newStats[levelIndex].stats[statIndex][field] = value;
    }
    setEditingStats(newStats);
  };

  const handleRemoveStat = (levelIndex, statIndex) => {
    const newStats = [...editingStats];
    newStats[levelIndex].stats.splice(statIndex, 1);
    setEditingStats(newStats);
  };

  // Add new course level in edit mode
  const handleAddLevelInEdit = () => {
    const existingLevels = editingStats.map(d => d.level);
    const availableLevels = COURSE_LEVELS.filter(l => !existingLevels.includes(l.value));
    const nextLevel = availableLevels.length > 0 ? availableLevels[0].value : "UG";
    const currentYear = new Date().getFullYear();
    setEditingStats([...editingStats, {
      level: nextLevel,
      stats: [{
        year: currentYear + "-" + String(currentYear + 1).slice(-2),
        highest_package: "",
        average_package: "",
        median_package: "",
        placement_rate: "",
        total_offers: "",
        companies_visited: "",
        top_recruiters: ""
      }]
    }]);
  };

  // Change course level in edit mode
  const handleEditLevelChange = (levelIndex, newLevel) => {
    const newStats = [...editingStats];
    newStats[levelIndex].level = newLevel;
    setEditingStats(newStats);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      // Filter out empty levels
      const dataToSave = editingStats.filter(level => level.stats.length > 0);
      await placementStatsAPI.update(editingCollege.college_id, {
        placement_data: dataToSave
      });
      toast.success('Placement statistics saved successfully');
      setEditDialogOpen(false);
      fetchStats();
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save placement statistics');
    } finally {
      setSaving(false);
    }
  };

  // Add new handlers
  const handleOpenAddDialog = () => {
    setSelectedCollegeId('');
    setNewPlacementData([
      {
        level: 'UG',
        stats: [{
          year: new Date().getFullYear().toString(),
          highest_package: '',
          average_package: '',
          median_package: '',
          placement_rate: '',
          total_offers: '',
          companies_visited: '',
          top_recruiters: ''
        }]
      }
    ]);
    setAddDialogOpen(true);
  };

  const handleAddLevel = () => {
    const existingLevels = newPlacementData.map(d => d.level);
    const availableLevels = COURSE_LEVELS.filter(l => !existingLevels.includes(l.value));
    const nextLevel = availableLevels.length > 0 ? availableLevels[0].value : 'UG';
    setNewPlacementData([...newPlacementData, {
      level: nextLevel,
      stats: [{
        year: new Date().getFullYear().toString(),
        highest_package: '',
        average_package: '',
        median_package: '',
        placement_rate: '',
        total_offers: '',
        companies_visited: '',
        top_recruiters: ''
      }]
    }]);
  };

  const handleNewStatChange = (levelIndex, statIndex, field, value) => {
    const newData = [...newPlacementData];
    if (field === 'placement_rate' || field === 'total_offers' || field === 'companies_visited') {
      newData[levelIndex].stats[statIndex][field] = value === '' ? null : parseFloat(value);
    } else {
      newData[levelIndex].stats[statIndex][field] = value;
    }
    setNewPlacementData(newData);
  };

  const handleNewLevelChange = (levelIndex, newLevel) => {
    const newData = [...newPlacementData];
    newData[levelIndex].level = newLevel;
    setNewPlacementData(newData);
  };

  const handleAddYearToNewLevel = (levelIndex) => {
    const newData = [...newPlacementData];
    newData[levelIndex].stats.push({
      year: new Date().getFullYear().toString(),
      highest_package: '',
      average_package: '',
      median_package: '',
      placement_rate: '',
      total_offers: '',
      companies_visited: '',
      top_recruiters: ''
    });
    setNewPlacementData(newData);
  };

  const handleRemoveNewStat = (levelIndex, statIndex) => {
    const newData = [...newPlacementData];
    newData[levelIndex].stats.splice(statIndex, 1);
    if (newData[levelIndex].stats.length === 0) {
      newData.splice(levelIndex, 1);
    }
    setNewPlacementData(newData);
  };

  const handleCreatePlacementStats = async () => {
    if (!selectedCollegeId) {
      toast.error('Please select a college');
      return;
    }

    // Validate at least one stat entry
    const hasValidData = newPlacementData.some(level => 
      level.stats.some(stat => stat.year && (stat.highest_package || stat.average_package))
    );
    
    if (!hasValidData) {
      toast.error('Please add at least one placement record with year and package details');
      return;
    }

    try {
      setSaving(true);
      await placementStatsAPI.update(selectedCollegeId, {
        placement_data: newPlacementData
      });
      toast.success('Placement statistics created successfully');
      setAddDialogOpen(false);
      fetchStats();
    } catch (error) {
      console.error('Failed to create:', error);
      toast.error('Failed to create placement statistics');
    } finally {
      setSaving(false);
    }
  };

  // Get colleges without placement data for the dropdown.
  // /api/admin/placement-stats returns college_id like "mysql-9629" while
  // /api/colleges returns id like "c-9629". Normalise both before
  // comparing -- otherwise the dropdown wrongly shows ALL colleges and the
  // "Without Placement Data" stat double-counts.
  const _stripPrefix = (id) => String(id || "").replace(/^(c-|mysql-)/i, "");
  const placedIds = new Set(stats.map(s => _stripPrefix(s.college_id)));
  const collegesWithoutStats = allColleges.filter(college =>
    !placedIds.has(_stripPrefix(college.id))
  );

  const formatPackage = (pkg) => pkg || '-';
  const formatRate = (rate) => (rate !== null && rate !== undefined) ? `${rate}%` : '-';

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-[#0F172A]">
              Placement Statistics
            </h1>
            <p className="text-sm text-[#475569] mt-1">
              Manage college placement data and statistics
            </p>
          </div>
          <Button 
            onClick={handleOpenAddDialog}
            className="bg-[#0066CC] hover:bg-[#0052A3]"
            data-testid="add-placement-stats-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Placement Stats
          </Button>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
              <Input
                placeholder="Search colleges..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="placement-search-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <Building2 className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-[#475569]">Total Colleges</p>
                  <p className="text-2xl font-bold text-[#0F172A]">{allColleges.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-50 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-[#475569]">With Placement Data</p>
                  <p className="text-2xl font-bold text-[#0F172A]">{stats.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-orange-50 rounded-lg">
                  <Building2 className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-[#475569]">Without Placement Data</p>
                  <p className="text-2xl font-bold text-[#0F172A]">{collegesWithoutStats.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Placement Stats Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-[#0066CC]" />
              College Placement Data
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-20 w-full" />
                ))}
              </div>
            ) : stats.length === 0 ? (
              <div className="text-center py-12 text-[#475569]">
                <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No placement statistics found</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={handleOpenAddDialog}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Placement Stats
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {stats.map((college) => (
                  <div key={college.college_id} className="border rounded-lg overflow-hidden">
                    {/* College Header */}
                    <div 
                      className="flex items-center justify-between p-4 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => toggleExpanded(college.college_id)}
                    >
                      <div className="flex items-center gap-3">
                        {expandedColleges[college.college_id] ? (
                          <ChevronDown className="h-5 w-5 text-[#475569]" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-[#475569]" />
                        )}
                        <Building2 className="h-5 w-5 text-[#0066CC]" />
                        <span className="font-medium text-[#0F172A]">{college.college_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {college.placement_data?.map((level) => (
                          <Badge key={level.level} variant="outline" className="text-xs">
                            {level.level}: {level.stats?.length || 0} years
                          </Badge>
                        ))}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(college);
                          }}
                          data-testid={`edit-placement-${college.college_id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Expanded Content */}
                    {expandedColleges[college.college_id] && (
                      <div className="p-4 border-t">
                        {college.placement_data?.length > 0 ? (
                          college.placement_data.map((levelData) => (
                            <div key={levelData.level} className="mb-6 last:mb-0">
                              <h4 className="font-medium text-[#0F172A] mb-3 flex items-center gap-2">
                                <GraduationCap className="h-4 w-4" />
                                {levelData.level} Placements
                              </h4>
                              {levelData.stats?.length > 0 ? (
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>Year</TableHead>
                                      <TableHead>Highest Package</TableHead>
                                      <TableHead>Average Package</TableHead>
                                      <TableHead>Median Package</TableHead>
                                      <TableHead>Placement Rate</TableHead>
                                      <TableHead>Total Offers</TableHead>
                                      <TableHead>Companies Visited</TableHead>
                                      <TableHead>Top Recruiters</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {levelData.stats.map((stat, idx) => (
                                      <TableRow key={idx}>
                                        <TableCell className="font-medium">{stat.year}</TableCell>
                                        <TableCell>{formatPackage(stat.highest_package)}</TableCell>
                                        <TableCell>{formatPackage(stat.average_package)}</TableCell>
                                        <TableCell>{formatPackage(stat.median_package)}</TableCell>
                                        <TableCell>{formatRate(stat.placement_rate)}</TableCell>
                                        <TableCell>{stat.total_offers || '-'}</TableCell>
                                        <TableCell>{stat.companies_visited || '-'}</TableCell>
                                        <TableCell className="max-w-xs truncate">{stat.top_recruiters || '-'}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              ) : (
                                <p className="text-sm text-[#475569]">No placement data for {levelData.level}</p>
                              )}
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-[#475569]">No placement data available</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Placement Statistics</DialogTitle>
            <DialogDescription>
              {editingCollege?.college_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {editingStats.map((levelData, levelIndex) => (
              <div key={levelData.level} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <GraduationCap className="h-4 w-4" />
                    <Select 
                      value={levelData.level} 
                      onValueChange={(val) => handleEditLevelChange(levelIndex, val)}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="Select Level" />
                      </SelectTrigger>
                      <SelectContent>
                        {COURSE_LEVELS.map((level) => (
                          <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-sm text-[#475569]">Placements</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleAddYearToLevel(levelIndex)}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Year
                  </Button>
                </div>

                <div className="space-y-4">
                  {levelData.stats?.map((stat, statIndex) => (
                    <div key={statIndex} className="grid grid-cols-10 gap-2 items-end border-b pb-4">
                      <div>
                        <Label className="text-xs">Year</Label>
                        <Select
                          value={stat.year || ''}
                          onValueChange={(val) => handleStatChange(levelIndex, statIndex, 'year', val)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select Year" />
                          </SelectTrigger>
                          <SelectContent>
                            {YEAR_OPTIONS.map((year) => (
                              <SelectItem key={year} value={year}>{year}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Highest Pkg</Label>
                        <Input
                          value={stat.highest_package || ''}
                          onChange={(e) => handleStatChange(levelIndex, statIndex, 'highest_package', e.target.value)}
                          placeholder="25 LPA"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Avg Pkg</Label>
                        <Input
                          value={stat.average_package || ''}
                          onChange={(e) => handleStatChange(levelIndex, statIndex, 'average_package', e.target.value)}
                          placeholder="8 LPA"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Median Pkg</Label>
                        <Input
                          value={stat.median_package || ''}
                          onChange={(e) => handleStatChange(levelIndex, statIndex, 'median_package', e.target.value)}
                          placeholder="6 LPA"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Rate %</Label>
                        <Input
                          type="number"
                          value={stat.placement_rate ?? ''}
                          onChange={(e) => handleStatChange(levelIndex, statIndex, 'placement_rate', e.target.value)}
                          placeholder="85"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Offers</Label>
                        <Input
                          type="number"
                          value={stat.total_offers ?? ''}
                          onChange={(e) => handleStatChange(levelIndex, statIndex, 'total_offers', e.target.value)}
                          placeholder="500"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Companies</Label>
                        <Input
                          type="number"
                          value={stat.companies_visited ?? ''}
                          onChange={(e) => handleStatChange(levelIndex, statIndex, 'companies_visited', e.target.value)}
                          placeholder="100"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Top Recruiters (comma separated)</Label>
                        <Input
                          value={stat.top_recruiters || ''}
                          onChange={(e) => handleStatChange(levelIndex, statIndex, 'top_recruiters', e.target.value)}
                          placeholder="TCS, Infosys, Google, Microsoft"
                          className="w-full"
                        />
                      </div>
                      <div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleRemoveStat(levelIndex, statIndex)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {(!levelData.stats || levelData.stats.length === 0) && (
                    <p className="text-sm text-[#475569] text-center py-4">
                      No placement records. Click "Add Year" to add one.
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {editingStats.length < COURSE_LEVELS.length && (
            <Button variant="outline" onClick={handleAddLevelInEdit} className="w-full mb-4">
              <Plus className="h-4 w-4 mr-2" />
              Add Another Course Level
            </Button>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add New Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Placement Statistics</DialogTitle>
            <DialogDescription>
              Add placement data for a college
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* College Selection */}
            <div>
              <Label>Select College</Label>
              <Select value={selectedCollegeId} onValueChange={setSelectedCollegeId}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Choose a college..." />
                </SelectTrigger>
                <SelectContent>
                  {collegesWithoutStats.length === 0 ? (
                    <SelectItem value="" disabled>All colleges have placement data</SelectItem>
                  ) : (
                    collegesWithoutStats.map((college) => (
                      <SelectItem key={college.id} value={college.id}>
                        {college.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {collegesWithoutStats.length === 0 && (
                <p className="text-sm text-amber-600 mt-2">
                  All colleges already have placement data. Use the edit button to modify existing data.
                </p>
              )}
            </div>

            {/* Placement Data Entry */}
            {newPlacementData.map((levelData, levelIndex) => (
              <div key={levelIndex} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <GraduationCap className="h-4 w-4" />
                    <Select 
                      value={levelData.level} 
                      onValueChange={(val) => handleNewLevelChange(levelIndex, val)}
                    >
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Select Level" />
                      </SelectTrigger>
                      <SelectContent>
                        {COURSE_LEVELS.map((level) => (
                          <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-sm text-[#475569]">Placements</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleAddYearToNewLevel(levelIndex)}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Year
                  </Button>
                </div>

                <div className="space-y-4">
                  {levelData.stats.map((stat, statIndex) => (
                    <div key={statIndex} className="grid grid-cols-10 gap-2 items-end border-b pb-4">
                      <div>
                        <Label className="text-xs">Year *</Label>
                        <Select
                          value={stat.year || ''}
                          onValueChange={(val) => handleNewStatChange(levelIndex, statIndex, 'year', val)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select Year" />
                          </SelectTrigger>
                          <SelectContent>
                            {YEAR_OPTIONS.map((year) => (
                              <SelectItem key={year} value={year}>{year}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Highest Pkg *</Label>
                        <Input
                          value={stat.highest_package || ''}
                          onChange={(e) => handleNewStatChange(levelIndex, statIndex, 'highest_package', e.target.value)}
                          placeholder="25 LPA"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Avg Pkg</Label>
                        <Input
                          value={stat.average_package || ''}
                          onChange={(e) => handleNewStatChange(levelIndex, statIndex, 'average_package', e.target.value)}
                          placeholder="8 LPA"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Median Pkg</Label>
                        <Input
                          value={stat.median_package || ''}
                          onChange={(e) => handleNewStatChange(levelIndex, statIndex, 'median_package', e.target.value)}
                          placeholder="6 LPA"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Rate %</Label>
                        <Input
                          type="number"
                          value={stat.placement_rate ?? ''}
                          onChange={(e) => handleNewStatChange(levelIndex, statIndex, 'placement_rate', e.target.value)}
                          placeholder="85"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Offers</Label>
                        <Input
                          type="number"
                          value={stat.total_offers ?? ''}
                          onChange={(e) => handleNewStatChange(levelIndex, statIndex, 'total_offers', e.target.value)}
                          placeholder="500"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Companies</Label>
                        <Input
                          type="number"
                          value={stat.companies_visited ?? ''}
                          onChange={(e) => handleNewStatChange(levelIndex, statIndex, 'companies_visited', e.target.value)}
                          placeholder="100"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Top Recruiters (comma separated)</Label>
                        <Input
                          value={stat.top_recruiters || ''}
                          onChange={(e) => handleNewStatChange(levelIndex, statIndex, 'top_recruiters', e.target.value)}
                          placeholder="TCS, Infosys, Google, Microsoft"
                          className="w-full"
                        />
                      </div>
                      <div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleRemoveNewStat(levelIndex, statIndex)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {newPlacementData.length < COURSE_LEVELS.length && (
              <Button variant="outline" onClick={handleAddLevel} className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                Add Another Course Level
              </Button>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreatePlacementStats} 
              disabled={saving || !selectedCollegeId}
              className="bg-[#0066CC] hover:bg-[#0052A3]"
            >
              {saving ? 'Creating...' : 'Create Placement Stats'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
